import { ConfigProvider } from "context/configContext";
import { useEffect, useState, Suspense } from "react";
import { lazyWithRetry } from "util/lazyWithRetry";

import { useLocation, useHistory } from "react-router-dom";
import { fetchAgeRestrictionPromptData } from "./util/DataUtil";
import "./App.css";
import "./variables.css";
import { isWebView } from "./commons/util/coreUtils";
import LoadCart from "./containers/loadCart/loadCart";
import LoadEnv from "./containers/loadEnv/loadEnv";
import { CartProvider } from "./context/cartContext";
import { FlightProvider } from "./context/FlightsContext";
import {
  removeLocalStorage,
  setLocalStorage,
  setSessionStorage,
} from "./util/storageUtil";
import LoadHeaderAndFooter from "containers/loadHeaderAndFooter/loadHeaderAndFooter";
import { getQueryParamValues } from "commons/util/helperFunctions";
import Loader from "components/atoms/loader";
import { ColorThemeProvider } from "containers/theme/colorTheme";
import { ImageAssetProvider } from "containers/theme/imageTheme";
import { OATProvider } from "context/OATContext";
import { initGA4 } from "util/analytics/ga4Ecommerce";

const RootSwitch = lazyWithRetry(() => import("./routes/RootSwitch"));

const fallback = () => {};

function App() {
  const location = useLocation();
  const history = useHistory();
  const [isEnvLoaded, setIsEnvLoaded] = useState(false);
  const [languageList, setLanguageList] = useState();

  useEffect(() => {
    // Non-critical logic deferred to improve TBT
    const idleTask = () => {
      fetchAgeRestrictionPromptData();
      checkPromoCodeInUrl();

      // MutationObserver for userway - non-critical
      const targetNode = document.body;
      const config = { childList: true, subtree: true };
      const callback = function (mutationsList, observer) {
        for (const mutation of mutationsList) {
          if (mutation.type === "childList") {
            const userwayTrigger = document.querySelector(".userway-trigger");
            if (userwayTrigger) {
              userwayTrigger.setAttribute("aria-label", "Accessibility Widget");
              observer.disconnect();
            }
          }
        }
      };
      const observer = new MutationObserver(callback);
      observer.observe(targetNode, config);

      // Campaign check
      const queryParams = new URLSearchParams(window.location.search);
      const campaign = queryParams.get("utm_campaign");
      if (campaign) {
        setSessionStorage("utm_campaign", campaign);
      }
    };

    if (window.requestIdleCallback) {
      window.requestIdleCallback(idleTask);
    } else {
      setTimeout(idleTask, 2000);
    }
  }, []);

  useEffect(() => {
    if (isEnvLoaded) {
      initGA4();
    }
  }, [isEnvLoaded]);

  // Memoize language check to avoid unnecessary re-runs
  useEffect(() => {
    if (!languageList) return;

    const langCode = location?.pathname?.split("/")[1];
    const isLangExist = languageList.some(
      (language) => language.prefix.replace("/", "") === langCode,
    );

    if (isLangExist) {
      setLocalStorage("langCode", langCode);
    } else {
      removeLocalStorage("langCode");
    }

    if (!isWebView()) {
      const viewport = document.getElementById("viewport");
      if (viewport) {
        viewport.content = "width=device-width, initial-scale=1";
      }
    }
  }, [languageList, location.pathname]);

  const checkPromoCodeInUrl = () => {
    const codes = getQueryParamValues("promoCode");
    if (codes && codes.length > 0) {
      setSessionStorage("scannedPromoCode", codes[0]?.toUpperCase());
    }
  };

  if (!isEnvLoaded) {
    return (
      <LoadEnv isEnvLoaded={isEnvLoaded} setIsEnvLoaded={setIsEnvLoaded} />
    );
  }

  return (
    <ConfigProvider isEnvLoaded={isEnvLoaded} setIsCurrencyLoaded={fallback}>
      <OATProvider setLanguageList={setLanguageList}>
        <ImageAssetProvider setIsImageAssets={fallback}>
          <ColorThemeProvider setIsColorTheme={fallback}>
            <CartProvider>
              <FlightProvider>
                <LoadCart setIsCartLoaded={fallback} />
                <LoadHeaderAndFooter setIsHeaderAndFooterLoaded={fallback} />
                <Suspense fallback={<Loader />}>
                  <RootSwitch />
                </Suspense>
              </FlightProvider>
            </CartProvider>
          </ColorThemeProvider>
        </ImageAssetProvider>
      </OATProvider>
    </ConfigProvider>
  );
}

export default App;
